import React, {useEffect, useRef, useState} from "react";
import {
  Button, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Stack, TextField, Typography
} from "@mui/material";
import {HugeiconsIcon} from "@hugeicons/react";
import {Call02Icon} from "@hugeicons/core-free-icons";
import {useWebrtc} from "../../context/WebrtcContext";
import {useRegistryProject} from "../../pages/registry-project/RegistryProjectContext";
import {subProjectLabel} from "../../utils/subProjects";

const fieldSx = {"& .MuiOutlinedInput-root": {borderRadius: "10px", fontSize: 14}};

// a phone number: optional leading +, then digits (spaces, dashes, dots and brackets are ignored)
const normalize = (value) => String(value || "").trim().replace(/[\s\-().]/g, "");
const isPhoneNumber = (value) => /^\+?\d{3,15}$/.test(normalize(value));

const outboundOf = (subProject) => subProject?.coreConfiguration?.outbound_call || "";

// "Call a number" from the browser phone: this browser rings first (answered automatically), then the number is
// dialed from the chosen sub project's outbound caller ID, and the two are bridged once it answers.
export const DialNumberDialog = ({open, onClose}) => {
  const {project, subProject} = useRegistryProject();
  const {status, callCustomer} = useWebrtc();
  const subProjects = project?.subProjects || [];
  const [number, setNumber] = useState("");
  const [subProjectId, setSubProjectId] = useState("");
  const [touched, setTouched] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setNumber("");
    setTouched(false);
    setSubProjectId(String(subProject?.id || subProjects[0]?.id || ""));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const chosen = subProjects.find(item => String(item.id) === String(subProjectId)) || subProject;
  const valid = isPhoneNumber(number);
  const connected = status === "registered";

  const submit = (e) => {
    e?.preventDefault();
    setTouched(true);
    if (!valid || !connected) return;
    onClose();
    callCustomer({projectId: project?.id, subProjectId: subProjectId || undefined, phoneNumber: normalize(number)});
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth={"xs"}
      // focused once the dialog is in: autoFocus alone loses to the header menu handing focus back to its button
      TransitionProps={{onEntered: () => inputRef.current?.focus()}}
      PaperProps={{
      component: "form", onSubmit: submit, sx: {borderRadius:   "18px"},
    }}>
      <DialogTitle sx={{fontSize: 17, fontWeight: 700, color: "#23264D", pb: 0.5}}>Call a number</DialogTitle>
      <DialogContent>
        <Typography sx={{fontSize: 13, color: "#8A8AA0", mb: 2}}>
          Your browser rings first, then the number is dialed. You are connected as soon as they answer.
        </Typography>
        <Stack gap={2}>
          <TextField
            autoFocus inputRef={inputRef} fullWidth size={"small"} label={"Phone number"} placeholder={"+389 70 123 456"}
            value={number} onChange={(e) => setNumber(e.target.value)} onBlur={() => setTouched(true)}
            inputProps={{inputMode: "tel", autoComplete: "tel"}}
            error={touched && !valid}
            helperText={touched && !valid ? "Enter a phone number, digits with an optional leading +" : " "}
            sx={fieldSx}
          />
          {subProjects.length > 1 && (
            <TextField
              select fullWidth size={"small"} label={"Call from"} value={subProjectId}
              onChange={(e) => setSubProjectId(e.target.value)} sx={fieldSx}
            >
              {subProjects.map(item => (
                <MenuItem key={item.id} value={String(item.id)} sx={{fontSize: 14}}>{subProjectLabel(item)}</MenuItem>
              ))}
            </TextField>
          )}
          <Typography sx={{fontSize: 12, color: outboundOf(chosen) ? "#8A8AA0" : "#C73A3A", mt: -1}}>
            {outboundOf(chosen)
              ? `Caller ID: ${outboundOf(chosen)}`
              : "No outbound caller ID is set for this sub project (Settings → Outbound Caller ID)."}
          </Typography>
          {!connected && (
            <Typography sx={{fontSize: 12, color: "#C73A3A"}}>Your browser phone is not connected.</Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{px: 3, pb: 2.5}}>
        <Button onClick={onClose} sx={{textTransform: "none", fontWeight: 600, color: "#4A4A63", borderRadius: "10px"}}>
          Cancel
        </Button>
        <Button
          type={"submit"} variant={"contained"} disabled={!connected}
          startIcon={<HugeiconsIcon icon={Call02Icon} size={16}/>}
          sx={{
            textTransform: "none", fontWeight: 600, borderRadius: "10px", boxShadow: "none", bgcolor: "#1E8A55",
            "&:hover": {bgcolor: "#18734A", boxShadow: "none"},
          }}
        >
          Call
        </Button>
      </DialogActions>
    </Dialog>
  );
};
